import * as Yup from "yup";
import { ExportSite } from "constants/exportSites";
import { AdvancedDraftingValidationStates } from "constants/draftingAttributes";
import { Species } from "constants/species";
import {
  diffBreedHdAllocations,
  diffFatScoreHdAllocations,
} from "lib/validation/allocation";
import { BaseSchema } from "yup";
import { SchemaLike } from "yup/lib/types";
import {
  ErrorDict,
  doValidate,
  vendorRequiredValidation,
  sheepFieldsRequired,
  pregStatusValidation,
  sheepDraftingAttributesRequired,
  hornValidation,
  cattleAgeRequiredValidation,
} from "./doValidate";

/* Validation using the allocation  above */
const fatScoreValidation = {
  fatScoreComplete: Yup.number().test(
    "fatScoretest",
    "Fat Score incomplete.",
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Yup TestContext missing 'from' property
    (_ignored, context: any) => {
      if (!context) {
        return false;
      }
      return diffFatScoreHdAllocations(context.from[1].value) === 0;
    },
  ),
};

const breedAllocationValidation = {
  advancedBreedComplete: Yup.number().test(
    "breedAllocationTest",
    "Breed Allocation incomplete.",
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Yup TestContext missing 'from' property
    (_ignored, context: any) => {
      if (!context) {
        return false;
      }
      return diffBreedHdAllocations(context.from[1].value) === 0;
    },
  ),
};

const cattleRequiredFieldsValidation = {
  breed_id: Yup.number()
    .typeError("Breed is required")
    .required("Breed is required"),
};

const auctionsPlusSheepSchema = Yup.object().shape({
  ...vendorRequiredValidation,
  ...sheepFieldsRequired,

  estimatedAverageMassGrams: Yup.string()
    .typeError("Estimated Average Mass is required.")
    .required("Estimated Average Mass required."),

  draftingAttributes: Yup.object({
    ...sheepDraftingAttributesRequired,
    ...fatScoreValidation,
    ...pregStatusValidation,

    weighLocation: Yup.string()
      .typeError("Weigh Location is required.")
      .required("Weigh Location required."),
  }).typeError("Advanced Drafting Details incomplete"),
});

const auctionsPlusCattleSchema = Yup.object().shape({
  ...vendorRequiredValidation,
  ...cattleRequiredFieldsValidation,

  draftingAttributes: Yup.object({
    ...breedAllocationValidation,
    ...hornValidation,
    ...cattleAgeRequiredValidation,
    ...fatScoreValidation,
  }).typeError("Advanced Drafting Details incomplete"),
});

interface MachineryDescriptionFilter {
  category?: string;
  itemType?: string;
  subType?: string;
}

const regoWhitelist: MachineryDescriptionFilter[] = [
  // All trailers (sourced by looking through auctions_plus_item_types.js)
  { itemType: "Trailer" },
  { itemType: "Marine Trailer" },
  // All Vintage vehicles
  { subType: "Vintage" },
];

// when registration is set and the vehicle type is not a trailer or a marine trailer or the subtype is vintage,
// require the user to enter make, model, year, vin, and either odometer or hours

type YupSchema = SchemaLike | ((schema: BaseSchema) => SchemaLike);

function whenRegoSetAndNotTrailerFilter(
  baseSchema: BaseSchema,
  options: { then?: YupSchema; otherwise?: YupSchema },
) {
  return baseSchema.when(["registration", "itemType", "subType"], {
    is: (registration, itemType, subType) => {
      if (registration) {
        if (!itemType && !subType) {
          // Rego has been entered, but not the item type and subtype,
          // we need the user to fill out the vehicle details
          return true;
        }

        // Rego, item type and subtype have been entered
        // we need the user to fill out the vehicle details unless it's vintage or a trailer
        return !regoWhitelist.some(
          whitelist =>
            (whitelist.itemType && whitelist.itemType === itemType) ||
            (whitelist.subType && whitelist.subType === subType),
        );
      }
      return false;
    },
    then: options?.then || undefined,
    otherwise: options?.otherwise || undefined,
  });
}

function required(errorMessage: string) {
  return (schema: BaseSchema) => schema.required(errorMessage);
}

function requiredThen(errorMessage: string) {
  return { then: required(errorMessage) };
}

const auctionsPlusClearingSaleValidation = {
  status: Yup.string().required("Status is required"),
  registration: Yup.string().nullable(),
  category: Yup.string().nullable(),
  itemType: Yup.string().nullable(),
  subType: Yup.string().nullable(),
  make: whenRegoSetAndNotTrailerFilter(
    Yup.string().nullable(),
    requiredThen("Make is required"),
  ),
  model: whenRegoSetAndNotTrailerFilter(
    Yup.string().nullable(),
    requiredThen("Model is required"),
  ),
  year: whenRegoSetAndNotTrailerFilter(
    Yup.date().nullable(),
    requiredThen("Year is required"),
  ),
  vin: whenRegoSetAndNotTrailerFilter(
    Yup.string().nullable(),
    requiredThen("VIN is required"),
  ),
  odometer: whenRegoSetAndNotTrailerFilter(Yup.string().nullable(), {
    then: (schema: BaseSchema) =>
      schema.when("hours", {
        is: (hours: number) => !hours && hours !== 0, // It may legitimately have zero hours, without the test it will fail on the falsy check
        then: required("Odometer or Hours is required"),
      }),
  }),
  hours: whenRegoSetAndNotTrailerFilter(Yup.number().nullable(), {
    then: (schema: BaseSchema) =>
      schema.when("odometer", {
        is: (odometer: number) => !odometer && odometer !== 0, // It may legitimately have zero hours, without the test it will fail on the falsy check,
        then: required("Odometer or Hours is required"),
      }),
  }),
};

const auctionsPlusClearingSaleSchema = Yup.object().shape({
  clearingSaleAttributes: Yup.object().shape(
    auctionsPlusClearingSaleValidation,
    // More poorly documented/untyped features of the Yup API,
    // declare a list of fields to lazy validate, avoiding circular dependencies
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Yup circular dependency declaration
    [["odometer", "hours"]] as any,
  ),
});

export const getAuctionsPlusValidationErrorsOrStatus = (
  saleLot: SaleLot,
): string | ErrorDict => {
  if (!saleLot.exportSites?.includes(ExportSite.AUCTIONS_PLUS)) {
    return AdvancedDraftingValidationStates.DISABLED;
  }

  if (!saleLot.sale_round) {
    return {
      sale_round: "No round selected",
    };
  }

  let validationSchema = null;

  if (saleLot.sale_round.species_id === Species.CATTLE) {
    validationSchema = auctionsPlusCattleSchema;
  } else if (saleLot.sale_round.species_id === Species.SHEEP) {
    validationSchema = auctionsPlusSheepSchema;
  } else if (saleLot.sale_round.species_id === Species.CLEARING_SALE) {
    validationSchema = auctionsPlusClearingSaleSchema;
  }

  if (validationSchema) {
    return (
      doValidate(validationSchema, saleLot) ||
      AdvancedDraftingValidationStates.SUCCESS
    );
  }
  return AdvancedDraftingValidationStates.DISABLED;
};
